import { MessageType } from 'ts-components/Chat';

export const ChatRole = {
  ASSISTANT: 'assistant',
  USER: 'user',
  SYSTEM: 'system',
} as const;

export const FlowStatus = {
  SUCCESS: 'success',
  FAILED: 'failed',
} as const;

export const AnswerMode = {
  SINGLE_SELECT: 'single_select',
  MULTI_SELECT: 'multi_select',
  NONE: '',
} as const;

export type ChatRoleType = (typeof ChatRole)[keyof typeof ChatRole];

export type FlowStatusType = (typeof FlowStatus)[keyof typeof FlowStatus];

export const OutputVariableName = {
  USER_OUTPUT: 'user_output',
} as const;

export type ParsedFlowContent = {
  id?: string;
  flow_id?: string;
  next_actions?: Array<{ label: string; url: string }>;
  nextActions?: string[];
  status?: string;
  message?: string;
};

export type AnswersOrAlertsResult = {
  messages: MessageType[];
  answers: string[];
  alert: string;
  numberOfNewMessages: number;
};

export type OutputVariableRequirements = {
  name: string;
  is_required: boolean;
  is_multiple: boolean;
  include_in_response: boolean;
};

export type ChatMessageData = {
  alert: string[] | string;
  answer: string[] | string;
  answer_mode: (typeof AnswerMode)[keyof typeof AnswerMode];
  app_requirements: string[] | string;
  business_usecase: string[] | string;
  question: string[] | string;
  strategic_plan: string[] | string;
  text: string;
  user_output: string;
};

export type ChatMessageDataStreaming = {
  alert: string[] | string;
  answer: string[] | string;
  answer_mode: (typeof AnswerMode)[keyof typeof AnswerMode];
  app_requirements: string[] | string;
  business_usecase: string[] | string;
  question: string[] | string;
  strategic_plan: string[] | string;
  text: string;
  user_output: string;
  raw_content: string;
  ready_for_action: boolean;
};

export type StreamMessageType = {
  is_streaming: boolean;
  message_type: string;
  value: string | ChatMessageFromAPI;
  raw_text?: string;
};

export type ChatStreamType = {
  id: string;
  message: any;
  onInitialSend: () => void;
  onMessage: (data: string, messageType?: string) => void;
  onRefine: () => void;
  onFinal: (data: ChatMessageDataStreaming) => void;
  onError: (error: any) => void;
  additonalEventListeners?: string;
};

export type ChatMessageFromAPI = {
  id: string;
  created: string;
  data: ChatMessageData;
  raw_content: string;
  order: number;
  role: ChatRoleType;
  ready_for_action: boolean;
  extracted_variables: ExtractedVariables;
};

export type ExtractedVariables = Record<string, unknown>;

export type ChatSessionFromAPI = {
  id: string;
  created: string;
  messages: ChatMessageFromAPI[];
  extracted_variables: ExtractedVariables;
  ready_for_action: boolean;
};

export type MappedChatSession = {
  messages: MessageType[];
  answers: string[];
  answer_mode: (typeof AnswerMode)[keyof typeof AnswerMode];
  alert: string;
  ready_for_action: boolean;
  numberOfNewMessages: number;
  last_flow?: ParsedFlowContent;
};

export type Initiator = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
};

export type FieldGroup = {
  name: string;
  nb_fields: number;
};

export type CustomObject = {
  name: string;
  field_groups: FieldGroup[];
};

export type CustomObjectGeneration = {
  elapsed_time: string;
  custom_objects: CustomObject[];
};

export type RunMetadata = {
  custom_object_generation: CustomObjectGeneration;
};

export type FlowStatusFromAPI = {
  id: string;
  business_id: string;
  initiator: Initiator;
  type: string;
  status: string;
  created: string;
  updated: string;
  run_metadata: RunMetadata;
  running_config: Record<string, unknown>;
  completed_at: string | null;
};
